import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { apiFetch, getUserDisplayName } from '../utils/auth';
import { formatRepairNumber } from '../utils/format';

const API_BASE = 'https://vhnsv33dvk.execute-api.eu-west-1.amazonaws.com';

const STATUS_LABELS = {
  initial_inspection:  'Initial Inspection',
  estimate_pending:    'Estimate Pending',
  estimate_sent:       'Estimate Shared',
  estimate_approved:   'Estimate Approved',
  waiting_parts:       'Waiting Parts',
  in_progress:         'In Progress',
  on_test:             'On Test',
  completed:           'Ready for Collection',
  collected:           'Collected',
  cancelled:           'Cancelled',
  returned:            'Returned',
  return_in_progress:  'Return In Progress',
  no_repair:           'No Repair'
};

const STATUS_COLOURS = {
  initial_inspection:  '#95a5a6',
  estimate_pending:    '#e67e22',
  estimate_sent:       '#f39c12',
  estimate_approved:   '#27ae60',
  waiting_parts:       '#8e44ad',
  in_progress:         '#2980b9',
  on_test:             '#16a085',
  completed:           '#27ae60',
  collected:           '#7f8c8d',
  cancelled:           '#c0392b',
  returned:            '#e74c3c',
  return_in_progress:  '#c0392b',
  no_repair:           '#7f8c8d'
};

function getDateRange(centreDate) {
  if (!centreDate) return { date_from: null, date_to: null };
  const centre = new Date(centreDate);
  const from = new Date(centre);
  const to = new Date(centre);
  from.setMonth(from.getMonth() - 2);
  to.setMonth(to.getMonth() + 2);
  return {
    date_from: from.toISOString(),
    date_to: to.toISOString()
  };
}

function Search({ token, onLogout }) {
  const navigate = useNavigate();
  const displayName = getUserDisplayName(token);

  const handleLogoutClick = () => {
    if (window.confirm('Are you sure you want to log out?')) {
      onLogout();
    }
  };
  
  const [query, setQuery] = useState({
        name: '',
        phone: '',
        postcode: '',
        repair_number: '',
        q: '',
        centre_date: ''
    });

  const [results, setResults] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const handleSearch = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError(null);
    setResults(null);

    // Build query string from non-empty fields
    const params = new URLSearchParams();
    Object.entries(query).forEach(([key, value]) => {
    if (key === 'centre_date') return; // handled separately
    if (value.trim()) params.append(key, value.trim());
    });

    if (query.centre_date) {
    const { date_from, date_to } = getDateRange(query.centre_date);
    if (date_from) params.append('date_from', date_from);
    if (date_to) params.append('date_to', date_to);
    }

    if (!params.toString()) {
      setError('Please enter at least one search term.');
      setLoading(false);
      return;
    }

    try {
      const response = await apiFetch(
        `${API_BASE}/repairs/search?${params.toString()}`,
        {},
        onLogout
      );
      if (!response) return;

      const data = await response.json();

      if (response.ok) {
        setResults(data.results || []);
      } else {
        setError(data.error || 'Search failed.');
      }
    } catch (err) {
      setError('Unable to connect. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleClear = () => {
    setQuery({ name: '', phone: '', postcode: '', repair_number: '', q: '', centre_date: '' });
    setResults(null);
    setError(null);
};

  return (
    <div className="portal">
      <nav className="portal-nav">
        <button className="portal-nav-brand" onClick={() => navigate('/admin')}>Repair Shop</button>
        <div className="portal-nav-links">
          <button className="nav-btn" onClick={() => navigate('/admin')}>Dashboard</button>
          <button className="nav-btn active">Search</button>
          <button className="nav-btn" onClick={() => navigate('/admin/repairs/new')}>New Repair</button>
          <button className="nav-btn logout" onClick={handleLogoutClick}>{displayName || 'Logout'}</button>
        </div>
      </nav>

      <div className="portal-content">
        <div className="portal-header">
          <h1>Search Repairs</h1>
        </div>

        <div className="detail-card" style={{ marginBottom: '1.5rem' }}>
          <form onSubmit={handleSearch}>
            <div className="search-grid">
              <div className="intake-field">
                <label>Customer Name</label>
                <input
                  type="text"
                  value={query.name}
                  onChange={(e) => setQuery(prev => ({ ...prev, name: e.target.value }))}
                  placeholder="Full or partial name"
                />
              </div>
              <div className="intake-field">
                <label>Phone Number</label>
                <input
                  type="text"
                  value={query.phone}
                  onChange={(e) => setQuery(prev => ({ ...prev, phone: e.target.value }))}
                  placeholder="Full or partial number"
                />
              </div>
              <div className="intake-field">
                <label>Postcode</label>
                <input
                  type="text"
                  value={query.postcode}
                  onChange={(e) => setQuery(prev => ({ ...prev, postcode: e.target.value }))}
                  placeholder="e.g. NN13 7DZ"
                />
              </div>
              <div className="intake-field">
                <label>Repair Number</label>
                <input
                  type="text"
                  value={query.repair_number}
                  onChange={(e) => setQuery(prev => ({ ...prev, repair_number: e.target.value }))}
                  placeholder="e.g. 1001"
                />
              </div>
              <div className="intake-field" style={{ gridColumn: '1 / -1' }}>
                <label>Free Text Search</label>
                <input
                  type="text"
                  value={query.q}
                  onChange={(e) => setQuery(prev => ({ ...prev, q: e.target.value }))}
                  placeholder="Search repair descriptions, fault reports, notes..."
                />
              </div>
            </div>

            <div className="intake-field">
                <label>Date (± 2 months)</label>
                <input
                    type="date"
                    value={query.centre_date}
                    onChange={(e) => setQuery(prev => ({ ...prev, centre_date: e.target.value }))}
                />
            </div>

            <div className="search-actions">
              <button type="submit" disabled={loading}>
                {loading ? 'Searching...' : 'Search'}
              </button>
              <button type="button" className="cancel-btn" onClick={handleClear}>
                Clear
              </button>
            </div>
          </form>
        </div>

        {error && <p className="error-text">{error}</p>}

        {results !== null && (
          <div>
            <p className="search-count">
              {results.length === 0
                ? 'No repairs found matching your search.'
                : `${results.length} repair${results.length === 1 ? '' : 's'} found`}
            </p>

            <div className="repairs-list">
              {results.map(repair => (
                <div
                  key={repair.repair_number}
                  className="repair-card"
                  onClick={() => navigate(`/admin/repairs/${repair.repair_number}`)}
                >
                  <div className="repair-card-header">
                    <div className="repair-card-title">
                      <span className="repair-number">#{formatRepairNumber(repair.repair_number)}</span>
                      <span className="repair-customer">{repair.customer_name}</span>
                    </div>
                    <span
                      className="repair-status"
                      style={{ background: STATUS_COLOURS[repair.status] }}
                    >
                      {STATUS_LABELS[repair.status]}
                    </span>
                  </div>
                  <div className="repair-card-body">
                    <p className="repair-item">
                      {repair.item_type}{repair.item_description ? ` — ${repair.item_description}` : ''}
                    </p>
                    {repair.customer_phone && (
                      <p className="repair-fault">{repair.customer_phone}</p>
                    )}
                    {repair.fault_reported && (
                      <p className="repair-fault">{repair.fault_reported}</p>
                    )}
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

export default Search;